import type { GraphEdge, GraphNode, GraphVisualizationFrame } from "@salesko/contracts";
import { Badge } from "@salesko/design-system";
import { AlertCircle, CheckCircle2, Loader2, Mail, Phone, Route } from "lucide-react";
import { useI18n, type Dictionary } from "../../i18n";
import { compactDateTime } from "../lib/format";
import { reminderWorkspaceAction } from "../lib/relationshipReminders";
import type { RelationshipIntelligenceState } from "../types";

type PersonDetailField = {
  key: string;
  label: string;
  value: string;
};

export type PersonDetailProjection = {
  contact: PersonDetailField[];
  email: string | null;
  personal: PersonDetailField[];
  phone: string | null;
  relationship: PersonDetailField[];
};

type PersonDetailOverviewProps = {
  changeSetCreatePending: boolean;
  currentFrameRecordId: string | null;
  frame: GraphVisualizationFrame | null;
  node: GraphNode;
  onLogReminderTouchpoint: (edge: GraphEdge) => void;
  onOpenReminderFrame: (target: { frameRecordId: string; nodeId: string }) => void;
  readOnly: boolean;
  relationshipIntelligence: RelationshipIntelligenceState;
  tags: string[];
};

export function personDetailProjection(node: GraphNode, t: Dictionary): PersonDetailProjection {
  const email = personDataString(node, "email");
  const phone = personDataString(node, "phone");
  const location = personDataString(node, "location");
  const company = personDataString(node, "company");
  const role = personDataString(node, "role");
  const stage = personDataString(node, "stage");
  const source = personDataString(node, "source");
  const interests = personDataString(node, "interests");
  const gender = personDataString(node, "gender");
  const birthday = personDataString(node, "birthday");

  return {
    contact: compactFields([
      { key: "email", label: t.detail.emailField, value: email },
      { key: "phone", label: t.detail.phoneField, value: phone },
      { key: "location", label: t.detail.locationField, value: location }
    ]),
    email,
    phone,
    relationship: compactFields([
      { key: "company", label: t.detail.companyField, value: company },
      { key: "role", label: t.detail.roleField, value: role },
      { key: "stage", label: t.detail.relationshipStageField, value: stage },
      { key: "source", label: t.detail.relationshipSourceField, value: source },
      { key: "interests", label: t.detail.interestsField, value: interests }
    ]),
    personal: compactFields([
      { key: "gender", label: t.detail.genderField, value: gender ? genderLabel(gender, t) : null },
      { key: "birthday", label: t.detail.birthdayField, value: birthday }
    ])
  };
}

export function PersonDetailOverview({
  changeSetCreatePending,
  currentFrameRecordId,
  frame,
  node,
  onLogReminderTouchpoint,
  onOpenReminderFrame,
  readOnly,
  relationshipIntelligence,
  tags
}: PersonDetailOverviewProps) {
  const { t } = useI18n();
  const projection = personDetailProjection(node, t);
  // Extraction confidence is maintenance data, not relationship context: it lives
  // in the detail panel's Advanced disclosure, never in this tag row.
  const contextTags = [...new Set(tags)];
  const reminder =
    relationshipIntelligence.reminderStatus === "success"
      ? relationshipIntelligence.reminders[0] ?? null
      : null;
  const reminderAction = reminder
    ? reminderWorkspaceAction(reminder, currentFrameRecordId, frame, node.id)
    : null;

  return (
    <>
      {projection.email || projection.phone ? (
        <nav className="person-quick-actions" aria-label={t.detail.contactActionsAriaLabel}>
          {projection.email ? (
            <a href={`mailto:${projection.email}`}>
              <Mail size={15} />
              {t.detail.emailAction}
            </a>
          ) : null}
          {projection.phone ? (
            <a href={`tel:${projection.phone}`}>
              <Phone size={15} />
              {t.detail.callAction}
            </a>
          ) : null}
        </nav>
      ) : null}

      {reminder ? (
        <section className="detail-section person-follow-up" aria-label={t.detail.followUp}>
          <p className="eyebrow">{t.detail.followUp}</p>
          <div className="person-follow-up-card">
            <div className="person-follow-up-metric">
              <strong>{t.detail.staleByDays(reminder.staleByDays)}</strong>
              <span>{t.relationshipIntel.lastSeenStaleBy(compactDateTime(reminder.lastSeenAt), reminder.staleByDays)}</span>
            </div>
            <p>{reminderOtherNodeLabel(reminder, node.id, frame)} · {reminder.edge.label}</p>
            {reminderAction?.kind === "log_touchpoint" && !readOnly ? (
              <button
                className="secondary-button person-follow-up-action"
                disabled={changeSetCreatePending}
                onClick={() => onLogReminderTouchpoint(reminderAction.edge)}
                title={t.relationshipIntel.recordTouchpointTitle}
                type="button"
              >
                {changeSetCreatePending ? <Loader2 className="spin" size={14} /> : <CheckCircle2 size={14} />}
                {t.relationshipIntel.logTouchpoint}
              </button>
            ) : reminderAction?.kind === "open_frame" ? (
              <button
                className="secondary-button person-follow-up-action"
                onClick={() =>
                  onOpenReminderFrame({
                    frameRecordId: reminderAction.frameRecordId,
                    nodeId: reminderAction.nodeId
                  })
                }
                title={t.relationshipIntel.openLastSeenFrameTitle}
                type="button"
              >
                <Route size={14} />
                {t.relationshipIntel.openInFrame}
              </button>
            ) : null}
          </div>
        </section>
      ) : null}
      {relationshipIntelligence.canQuery && relationshipIntelligence.reminderStatus === "loading" ? (
        <section className="detail-section person-follow-up" aria-label={t.detail.followUp}>
          <p className="eyebrow">{t.detail.followUp}</p>
          <p className="history-loading">
            <Loader2 className="spin" size={14} />
            {t.relationshipIntel.loadingStale}
          </p>
        </section>
      ) : null}
      {relationshipIntelligence.canQuery && relationshipIntelligence.reminderStatus === "error" ? (
        <section className="detail-section person-follow-up" aria-label={t.detail.followUp}>
          <p className="eyebrow">{t.detail.followUp}</p>
          <p className="history-error">
            <AlertCircle size={14} />
            {relationshipIntelligence.reminderError ?? t.relationshipIntel.remindersUnavailable}
          </p>
        </section>
      ) : null}

      {projection.contact.length > 0 ? (
        <PersonDetailSection label={t.detail.contact} fields={projection.contact} />
      ) : null}

      {contextTags.length > 0 || projection.relationship.length > 0 ? (
        <section className="detail-section person-detail-section" aria-label={t.detail.relationshipContext}>
          <p className="eyebrow">{t.detail.relationshipContext}</p>
          {contextTags.length > 0 ? (
            <div className="tag-row">
              {contextTags.map((tag) => (
                <Badge key={tag} tone="person">
                  {tag}
                </Badge>
              ))}
            </div>
          ) : null}
          <PersonFieldList fields={projection.relationship} />
        </section>
      ) : null}
    </>
  );
}

export function PersonPersonalDetails({ node }: { node: GraphNode }) {
  const { t } = useI18n();
  const fields = personDetailProjection(node, t).personal;
  return fields.length > 0 ? <PersonDetailSection label={t.detail.personalDetails} fields={fields} /> : null;
}

function PersonDetailSection({ fields, label }: { fields: PersonDetailField[]; label: string }) {
  return (
    <section className="detail-section person-detail-section" aria-label={label}>
      <p className="eyebrow">{label}</p>
      <PersonFieldList fields={fields} />
    </section>
  );
}

function PersonFieldList({ fields }: { fields: PersonDetailField[] }) {
  if (fields.length === 0) {
    return null;
  }

  return (
    <dl className="person-detail-fields">
      {fields.map((field) => (
        <div key={field.key}>
          <dt>{field.label}</dt>
          <dd>{field.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function personDataString(node: GraphNode, key: string) {
  const value = node.data[key];
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function compactFields(fields: Array<{ key: string; label: string; value: string | null }>) {
  return fields.flatMap((field) => (field.value ? [{ ...field, value: field.value }] : []));
}

function genderLabel(value: string, t: Dictionary) {
  switch (value) {
    case "male":
      return t.detail.genderOptions.male;
    case "female":
      return t.detail.genderOptions.female;
    case "other":
      return t.detail.genderOptions.other;
    case "undisclosed":
      return t.detail.genderOptions.undisclosed;
    default:
      return value;
  }
}

function reminderOtherNodeLabel(
  reminder: RelationshipIntelligenceState["reminders"][number],
  selectedNodeId: string,
  frame: GraphVisualizationFrame | null
) {
  const otherNodeId = reminder.edge.source === selectedNodeId ? reminder.edge.target : reminder.edge.source;
  const projectedNode = reminder.edge.source === selectedNodeId ? reminder.targetNode : reminder.sourceNode;
  return projectedNode?.label ?? frame?.graph.nodes.find((candidate) => candidate.id === otherNodeId)?.label ?? otherNodeId;
}
